import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { IAppState } from '../redux/store/types';
import { ICreateCommand, IReadCommand, IUpdateCommand, IDeleteCommand } from '../redux/crud/types';
import { IRecord, ViewMode, ILatestPublicRecord } from '../redux/records/types';
import { IReadSubrecordParams, ISubrecord } from '../redux/subrecords/types';
import { setUsageMetricsDataAction } from '../redux/analytics/actions';
import { setMenuSelectedPageAction, toggleMenuAction } from '../redux/menu/actions';
import { setUserTypeAction } from '../redux/auth/actions';
import { setWSConnectedAction, setAppVisibleAction, setConnectionsAndUsernamesAction, toggleConnectionsAction } from '../redux/websockets/actions';
import { setLLMProfilesAction } from '../redux/llmProfiles/actions';
import {
  setInitialLoadInProgressAction,
  addRecordAction,
  appendRecordsAction,
  setCurrentRecordAction,
  setRecordStateAction,
  deleteRecordAction,
  unlinkRecordTenantAction,
  setRecordConfirmedByBackendAction,
  resetRecordsAction,
  prepareReadRecordsCommandAction,
  setViewModeAction,
  setLatestPublicRecordsAction,
  addLatestPublicRecordsAction,
  updateLatestPublicRecordAction,
  removeLatestPublicRecordAction,
} from '../redux/records/actions';
import { setSubrecordAction, addSubrecordAction, deleteSubrecordAction, setSubrecordConfirmedByBackendAction } from '../redux/subrecords/actions';
import {
  prepareReadSaasTenantsCommandAction,
  prepareCreateSaasTenantCommandAction,
  setSaasTenantsAction,
  addSaasTenantAction,
  updateSaasTenantAction,
  updateSaasTenantUsageAction,
  setSaasTenantConfirmedByBackendAction,
  setSaasTenantSearchResultsAction,
  setSaasTenantSearchTooManyResultsAction,
} from '../redux/saasTenants/actions';
import appConfigData from '../appConfig.json';
import { Network } from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import i18n from '../i18n';
import { IConnectionAndUsername } from 'redux/websockets/types';
import { UserType } from '../redux/auth/types';
import { INewSaasTenant, ISaasTenant } from 'redux/saasTenants/types';
import { DEFAULT_VIEW } from 'redux/menu/types';
import { WEBSOCKETS_SESSION_ERROR_EVENT_NAME } from './Menu';
import { RecordAccessLevel } from 'data-types';
import {
  IWsCommandMessage,
  IWsCommand,
  IClientMetadata,
  IWsCreateRecordParams,
  IWsCreateSubrecordParams,
  IWsCreateSaasTenantParams,
  IWsReadRecordsParams,
  IWsReadSubrecordsParams,
  IWsReadSaasTenantsParams,
  IWsAnalyticsParams,
  IWsUpdateRecordParams,
  IWsUpdateSaasTenantParams,
  IWsDeleteRecordParams,
  IWsDeleteSubrecordParams,
  IWsDeleteSaasTenantParams,
  IWsDataReadPayload,
} from 'ws-messages';

class WebSocketService extends React.Component<IWebSocketProps, WebSocketState> {
  private webSocket: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private readonly MAX_RECONNECT_ATTEMPTS = 3;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;

  constructor(props: IWebSocketProps) {
    super(props);
    this.state = {
      previousMenuSelectedPage: null,
    };
  }

  async componentDidMount() {
    try {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    } catch (err) {
      console.error('Error initializing WebSocket service:', err);
      this.props.setWSConnectedAction(false);
    }
  }

  // Handle change from hidden to visible:
  handleVisibilityChange = () => {
    this.props.setAppVisibleAction(document.visibilityState === 'visible');
  };

  componentDidUpdate(prevProps: IWebSocketProps) {
    const { isWsConnected, JWT, isAppVisible, menuSelectedPage } = this.props;
    const { previousMenuSelectedPage } = this.state;

    // console.log(
    //   `isWsConnected: ${isWsConnected} (prev: ${prevProps.isWsConnected}), jwt: ${JWT?.substring(0, 10)}... (prev: ${prevProps.JWT?.substring(
    //     0,
    //     10,
    //   )}...), isAppVisible: ${isAppVisible} (prev: ${prevProps.isAppVisible}), menuSelectedPage: ${menuSelectedPage} (prev: ${prevProps.menuSelectedPage})`,
    // );

    if (!isWsConnected && JWT && isAppVisible) {
      // console.log('--> this.connect()');
      this.connect();

      // When reconnecting, set the menu selected page to the previous one if it exists
      if (previousMenuSelectedPage) setTimeout(() => this.props.setMenuSelectedPageAction(previousMenuSelectedPage), 5000); //TODO: Look into this again - without setTimeout the client opens several connections !
    } else if (!isAppVisible && prevProps.isAppVisible) {
      this.setState({ previousMenuSelectedPage: menuSelectedPage });
      if (menuSelectedPage) this.props.setMenuSelectedPageAction(null); // to trigger a refresh on reconnection.

      // console.log('--> this.disconnect()');
      this.disconnect();
    }

    this.compareAndUpload(prevProps);
  }

  // Compare the current props with the previous props, and upload to the backend via the websocket when applicable.
  private compareAndUpload(prevProps: IWebSocketProps) {
    const { createCommand, readCommand, updateCommand, deleteCommand, t } = this.props;

    const upload = (data: { command: IWsCommand }, retryCount = 0) => {
      try {
        const message: IWsCommandMessage = { action: 'CommandsHandlerRouteKey', data };
        const stringifiedData = JSON.stringify(message);
        // console.log(JSON.stringify(JSON.parse(stringifiedData), null, 2));

        if (this.webSocket?.readyState === WebSocket.OPEN) {
          this.webSocket.send(stringifiedData);
        } else if (retryCount < 3) {
          // WebSocket not ready, retry after delay
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.warn(`WebSocket not ready, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
          setTimeout(() => upload(data, retryCount + 1), delay);
        } else {
          toast.error(t('websocket.errors.sendFailed'), { autoClose: 5000 });
        }
      } catch (error) {
        toast.error(`Failed to send a message to the websocket server: ${error}`);
      }
    };

    // Helper function to upload data with metadata via WebSocket command
    const uploadWithMetadata = (type: IWsCommand['type'], key: 'records' | 'subrecords', baseParams: any): void => {
      const currentLanguage = i18n.language;

      const createEnrichedParams = (coords?: IClientMetadata['coords']) => ({
        ...baseParams,
        clientMetadata: {
          ...(coords && { coords }),
          language: currentLanguage,
        },
      });

      const uploadParams = (coords?: IClientMetadata['coords']) => {
        upload({ command: { type, params: { [key]: createEnrichedParams(coords) } } });
      };

      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            uploadParams({ latitude, longitude });
          },
          (error) => {
            console.error('Error getting location:', error);
            uploadParams();
          },
          { enableHighAccuracy: true },
        );
      } else {
        console.error('Geolocation not supported by this browser.');
        uploadParams();
      }
    };

    // CRUD: Commands to Create data:
    //-----------------------------------------
    if (createCommand && createCommand !== prevProps.createCommand) {
      const type = 'create';
      switch (createCommand.type) {
        case 'records':
          if (this.props.userRemainingCredit <= 0 && createCommand.params.user_text) toast.error(t('tokenUsage.noRemainingCredit'), { autoClose: false });
          else uploadWithMetadata(type, 'records', createCommand.params as IWsCreateRecordParams);
          break;
        case 'subrecords':
          if (this.props.userRemainingCredit <= 0 && createCommand.params.user_text) toast.error(t('tokenUsage.noRemainingCredit'), { autoClose: false });
          else uploadWithMetadata(type, 'subrecords', createCommand.params as IWsCreateSubrecordParams);
          break;
        case 'saasTenants':
          upload({ command: { type, params: { saasTenants: createCommand.params as IWsCreateSaasTenantParams } } });
          break;
        default:
          console.warn(`Unexpected create command type: ${createCommand.type}`);
          break;
      }
    }

    // CRUD: Commands to Read data:
    //-----------------------------------------
    if (readCommand && readCommand !== prevProps.readCommand) {
      const type = 'read';
      switch (readCommand.type) {
        case 'records':
          upload({ command: { type, params: { [readCommand.type]: readCommand.params as IWsReadRecordsParams } } });
          break;
        case 'subrecords':
          const record_id = (readCommand.params as IReadSubrecordParams).record_id;
          upload({ command: { type, params: { [readCommand.type]: { record_id } as IWsReadSubrecordsParams } } });
          break;
        case 'analytics':
          upload({ command: { type, params: { [readCommand.type]: readCommand.params as IWsAnalyticsParams } } });
          break;
        case 'saasTenants':
          upload({ command: { type, params: { [readCommand.type]: readCommand.params as IWsReadSaasTenantsParams } } });
          break;
        default:
          console.warn(`Unexpected read command type: ${readCommand.type}`);
          break;
      }
    }

    // CRUD: Commands to Update data:
    //-----------------------------------------
    if (updateCommand && updateCommand !== prevProps.updateCommand) {
      const type = 'update';
      switch (updateCommand.type) {
        case 'records':
          upload({ command: { type, params: { records: updateCommand.params as IWsUpdateRecordParams } } });
          break;
        case 'saasTenants':
          upload({ command: { type, params: { saasTenants: updateCommand.params as IWsUpdateSaasTenantParams } } });
          break;
        default:
          console.warn(`Unexpected update command type: ${updateCommand.type}`);
          break;
      }
    }

    // CRUD: Commands to Delete data:
    //-----------------------------------------
    if (deleteCommand && deleteCommand !== prevProps.deleteCommand) {
      const type = 'delete';
      switch (deleteCommand.type) {
        case 'records':
          upload({ command: { type, params: { records: deleteCommand.params as IWsDeleteRecordParams } } });
          break;
        case 'subrecords':
          upload({ command: { type, params: { subrecords: deleteCommand.params as IWsDeleteSubrecordParams } } });
          break;
        case 'saasTenants':
          upload({ command: { type, params: { saasTenants: deleteCommand.params as IWsDeleteSaasTenantParams } } });
          break;
        default: {
          console.warn(`Unexpected delete command type: ${deleteCommand.type}`);
          break;
        }
      }
    }
  }

  componentWillUnmount() {
    this.disconnect();
  }

  // Component's rendering function:
  render() {
    const { isWsConnected, showConnections, connectionsAndUsernames, lastConnectionsTimestamp, t } = this.props;

    return (
      <div
        className='network-container'
        title={isWsConnected ? t('websocket.tooltips.connected', { timestamp: lastConnectionsTimestamp }) : t('websocket.tooltips.disconnected')}
        onClick={() => this.props.toggleConnectionsAction(!showConnections)}
      >
        <div className='left-column'>
          <Network size={20} className={`network-icon ${isWsConnected ? 'connected' : 'disconnected'}`} />
          <span className='last-connections-timestamp'>{lastConnectionsTimestamp}</span>
        </div>
        <ul className='right-column'>
          {showConnections &&
            connectionsAndUsernames &&
            connectionsAndUsernames.map((item: IConnectionAndUsername) => (
              <li key={item.connectionId} className='username'>
                {item.username}
              </li>
            ))}
        </ul>
      </div>
    );
  }

  // Open a connection to the backend's websocket api:
  private async connect(): Promise<void> {
    const { JWT } = this.props;

    // Reset the previous connection (if open):
    this.disconnect();

    // Introduce a delay of 100 milliseconds before proceeding with the next line of code
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Open the new connection:
    const wsUrl = `${appConfigData.WEBSOCKET_API_URL}?token=${JWT}`;
    // console.log(`Creating WebSocket connection with URL: ${wsUrl}`);
    this.webSocket = new WebSocket(wsUrl);

    this.webSocket.onopen = () => {
      console.log('** WebSocket connection opened **');
      this.props.setWSConnectedAction(true);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
    };

    this.webSocket.onclose = (event) => {
      console.log(`** WebSocket connection closed **: ${JSON.stringify(event)}`);

      // Handle different close codes
      // 1006 = Abnormal closure (no close frame received)
      // 1008 = Policy violation (could be rate limiting)
      // 1011 = Server error (Lambda errors, including concurrency limits)
      const shouldReconnect = [1006, 1008, 1011].includes(event.code);

      if (shouldReconnect && this.props.isAppVisible && ++this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
        console.log(`Attempting to reconnect in ${this.reconnectDelay / 1000} seconds... (attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS})`);
        this.reconnectTimeout = setTimeout(() => {
          this.reconnectDelay *= 2;
          this.props.setWSConnectedAction(false);
        }, this.reconnectDelay);
      } else if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
        console.warn('Maximum reconnection attempts reached. Please refresh the page.');
        toast.error(this.props.t('websocket.errors.maxReconnectAttempts'), { autoClose: false });
      }
    };

    this.webSocket.onmessage = (event) => {
      this.handleWebsocketIncomingEvent(event.data);
    };

    this.webSocket.onerror = (error) => {
      console.error('** WebSocket error: **', error);
      this.props.setWSConnectedAction(false);

      // Trigger re-authentication flow through Menu component
      window.dispatchEvent(new Event(WEBSOCKETS_SESSION_ERROR_EVENT_NAME));
    };
  }

  // Close the connection to the backend's websocket api:
  private disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.webSocket) {
      this.webSocket.close();
      this.webSocket = null;
      this.props.setWSConnectedAction(false);
    }
  }

  // Main handler for incoming WebSocket messages
  private handleWebsocketIncomingEvent(eventData: string) {
    // console.log(JSON.stringify(JSON.parse(eventData), null, 2));
    const { userId, userEmail, userName, t } = this.props;

    const parsedEventData = JSON.parse(eventData);
    if (parsedEventData.userType) {
      if (parsedEventData.userType === UserType.Pending) {
        parsedEventData.userType = UserType.SaasTenant;
        const newSaaSTenant: INewSaasTenant = { saas_tenant_id: userId as string, email: userEmail as string, name: userName as string };
        this.props.prepareCreateSaasTenantCommandAction(newSaaSTenant);
        this.props.setMenuSelectedPageAction(DEFAULT_VIEW);
      }
      this.props.setUserTypeAction(parsedEventData.userType);
      if (parsedEventData.userType === UserType.Admin || parsedEventData.userType === UserType.SaasTenant) {
        this.props.prepareReadSaasTenantsCommandAction();
        this.props.setInitialLoadInProgressAction(true);
        this.props.resetRecordsAction();
        setTimeout(() => this.props.prepareReadRecordsCommandAction({ latestPublic: true }), 500);
        if (parsedEventData.userType === UserType.Admin) setTimeout(() => this.props.toggleConnectionsAction(true), 3000);
      }
      // if (parsedEventData.pendingEmailVerification) toast.info(t('websocket.notifications.emailVerificationPending'), { autoClose: 10000 });
    }

    // CRUD: event containing Created data
    if (parsedEventData.dataCreated) {
      this.handleDataCreated(parsedEventData.dataCreated);
    }
    // CRUD: event containing Read data
    else if (parsedEventData.dataRead) {
      this.handleDataRead(parsedEventData.dataRead);
      this.handleIncomingConnectionsAndUsernames(parsedEventData.connectionsAndUsernames);
    }
    // CRUD: event containing Updated data
    else if (parsedEventData.dataUpdated) {
      this.handleDataUpdated(parsedEventData.dataUpdated);
    }
    // CRUD: event containing Deleted data
    else if (parsedEventData.dataDeleted) {
      this.handleDataDeleted(parsedEventData.dataDeleted);
    } else if (parsedEventData.connectionsAndUsernames) {
      this.handleIncomingConnectionsAndUsernames(parsedEventData.connectionsAndUsernames);
    } else if (parsedEventData.pong) {
      this.handleIncomingConnectionsAndUsernames(null); // only updates lastConnectionsTimestamp*
    } else if (parsedEventData.dbAccess?.MAX_TIME_TO_WAIT_FOR_DB_OPERATION_MS) {
      toast.error('The database is temporarily unavailable. Please contact webcharm.tech@gmail.com to start the database.', { autoClose: 20000 });
    } else if (parsedEventData.progress) {
      // const { searchText, similarRecordsCount, similarSubrecordsCount } = parsedEventData.progress;
      // const message = searchText
      //   ? similarRecordsCount === undefined
      //     ? `${t('records.searching')} : ${`${searchText}`}`
      //     : `${t('common.wereFound')} ${similarRecordsCount} ${t('records.title')}${
      //         (similarSubrecordsCount && `+ ${similarSubrecordsCount} ${t('subrecords.title')}`) || ''
      //       } : ${`${searchText}`}`
      //   : '???';
      // console.log(message);
    } else if (parsedEventData.error) {
      const { message, request } = parsedEventData.error;
      toast.error(message === 'length' ? t('records.LLMLengthLimit') : message, { autoClose: 10000 });
      //TODO: Handle all requests
      if (request?.commandType === 'create') {
        if (request.commandParams.records) this.props.deleteRecordAction(request.commandParams.records.record_id);
        else if (request.commandParams.subrecords) {
          const { record_id, subrecord_id } = request.commandParams.subrecords;
          this.props.deleteSubrecordAction(record_id, subrecord_id);
        }
      }
    } else {
      console.warn(eventData.substring(0, 500));
    }
  }

  // CRUD: event containing Created data
  private handleDataCreated(dataCreated: any) {
    // console.log(JSON.stringify(dataCreated, null, 2));
    if (dataCreated.records) {
      const newReceivedRecord: IRecord = { ...dataCreated.records };
      const isNewRecord = !this.props.records.find((record) => record.record_id === newReceivedRecord.record_id);
      if (isNewRecord) {
        // Add the new record if owned by the current user OR explicitly shared with the current user.
        if (
          newReceivedRecord.saas_tenant_id === this.props.userId ||
          (newReceivedRecord.sharedSaasTenantIdsAndNames || []).some((shared) => shared.id === this.props.userId)
        )
          this.props.addRecordAction(newReceivedRecord);
        if (newReceivedRecord.access_level === RecordAccessLevel.Public) {
          // Create ILatestPublicRecord from the received record
          const latestPublicRecord = {
            updated_at: newReceivedRecord.updated_at || new Date().toISOString(),
            subject: newReceivedRecord.subject || '',
            record_id: newReceivedRecord.record_id,
            isLinkable: true,
            saas_tenant_id: newReceivedRecord.saas_tenant_id || '',
            saas_tenant_name: newReceivedRecord.saas_tenant_name || '',
            summaries: [],
          };
          this.props.addLatestPublicRecordsAction(latestPublicRecord);
        }
        this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
      } else {
        this.props.setRecordConfirmedByBackendAction(newReceivedRecord.record_id, {
          user_text: dataCreated.records.user_text,
          ai_text: dataCreated.records.ai_text,
          interim: dataCreated.records.interim,
          subject: dataCreated.records.subject,
          subrecordsCount: dataCreated.records.subrecordsCount,
          saas_tenant_id: newReceivedRecord.saas_tenant_id,
          ...(newReceivedRecord.saas_tenant_name ? { saas_tenant_name: newReceivedRecord.saas_tenant_name } : {}), // update only if received from the backend.
          ...(newReceivedRecord.sharedSaasTenantIdsAndNames ? { sharedSaasTenantIdsAndNames: newReceivedRecord.sharedSaasTenantIdsAndNames } : {}),
          updated_at: newReceivedRecord.updated_at as string,
        });
        this.props.updateLatestPublicRecordAction(newReceivedRecord.record_id);
      }
      setTimeout(() => this.props.prepareReadSaasTenantsCommandAction(), 10000); // TODO: (1) Add a prepareReadUsageCommandAction, (2) 10,000 is just a guess - it might take the backend shorter or longer to compute the cost..
    } else if (dataCreated.subrecords) {
      const newReceivedSubrecord = dataCreated.subrecords;
      const isNewSubrecord = !(this.props.subrecords[newReceivedSubrecord.record_id] || []).find(
        (subrecord) => subrecord.subrecord_id === newReceivedSubrecord.subrecord_id,
      );
      if (isNewSubrecord) {
        this.props.addSubrecordAction(newReceivedSubrecord);
        this.props.setCurrentRecordAction(newReceivedSubrecord.record_id);
        this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
      } else
        this.props.setSubrecordConfirmedByBackendAction(newReceivedSubrecord.record_id, newReceivedSubrecord.subrecord_id, {
          user_text: newReceivedSubrecord.user_text,
          ai_text: newReceivedSubrecord.ai_text,
          is_consolidated: newReceivedSubrecord.is_consolidated,
          interim: newReceivedSubrecord.interim,
          saas_tenant_id: newReceivedSubrecord.saas_tenant_id,
          saas_tenant_name: newReceivedSubrecord.saas_tenant_name,
        });
      setTimeout(() => this.props.prepareReadSaasTenantsCommandAction(), 10000); // TODO: (1) Add a prepareReadUsageCommandAction, (2) 10,000 is just a guess - it might take the backend shorter or longer to compute the cost..
    } else if (dataCreated.saasTenants) {
      const newReceivedSaasTenant = dataCreated.saasTenants;
      const isNewnewSaasTenant = !this.props.saasTenants.find((saasTenant) => saasTenant.saas_tenant_id === newReceivedSaasTenant.saas_tenant_id);
      if (isNewnewSaasTenant) this.props.addSaasTenantAction(newReceivedSaasTenant);
      else {
        this.props.setSaasTenantConfirmedByBackendAction(newReceivedSaasTenant.saas_tenant_id, newReceivedSaasTenant.updated_at);
        if (!dataCreated.saasTenants.disabled && this.props.userType === UserType.FFU) {
          // toggle to SaaS tenant view
          this.props.setUserTypeAction(UserType.SaasTenant);
          this.props.setMenuSelectedPageAction(DEFAULT_VIEW);
        }
      }
    }

    if (dataCreated.usage) this.props.updateSaasTenantUsageAction(this.props.userId as string, dataCreated.usage);
  }

  // CRUD: event containing Read data
  private handleDataRead(dataRead: IWsDataReadPayload) {
    const { showAllLinkable, searchText, t } = this.props;

    // console.log(dataRead);
    if (dataRead.llmProfiles) this.props.setLLMProfilesAction(dataRead.llmProfiles);
    if (dataRead.records) {
      if (dataRead.records.record_id) {
        this.props.setRecordStateAction({
          record_id: dataRead.records.record.record_id,
          user_text: dataRead.records.record.user_text,
          ai_text: dataRead.records.record.ai_text,
        });
      } else {
        this.props.appendRecordsAction(dataRead.records);
        this.props.setInitialLoadInProgressAction(false);
        if (dataRead.records.length === 0 && !dataRead.llmProfiles /* not the initial loading */)
          toast.info(
            `${t('records.NoChatsFound', {
              type: t(showAllLinkable ? 'records.linkable' : 'records.privateAndLinked'),
              similarity: (searchText && t('records.similar')) || '',
              next: !showAllLinkable ? t('records.CreateOrLink') : ' ...',
            })}`,
            { autoClose: 10000 },
          );
      }
    }
    if (dataRead.latestPublicRecords?.length > 0) this.props.setLatestPublicRecordsAction(dataRead.latestPublicRecords);
    if (dataRead.subrecords?.length > 0) this.props.setSubrecordAction(dataRead.subrecords[0].record_id, dataRead.subrecords);
    if (dataRead.saasTenants?.length > 0) {
      this.props.setSaasTenantsAction(dataRead.saasTenants);
    }

    if (dataRead.saasTenantSearchResults) {
      this.props.setSaasTenantSearchResultsAction(dataRead.saasTenantSearchResults);
      this.props.setSaasTenantSearchTooManyResultsAction(!!dataRead.saasTenantSearchTooManyResults);
    }

    if (dataRead.usage) {
      // TODO: Update the cost$ to the frontend after each AI access (currently piggybacked on every read!)
      const currentSaaSTenant = this.props.saasTenants.find((saasTenant) => saasTenant.saas_tenant_id === this.props.userId) as ISaasTenant;
      if (currentSaaSTenant) this.props.setSaasTenantsAction([{ ...currentSaaSTenant, usage: dataRead.usage }]);
    }
    if (dataRead.analytics?.usageMetrics) {
      this.props.setUsageMetricsDataAction(dataRead.analytics.usageMetrics);
    }
  }

  // CRUD: event containing Updated data
  private handleDataUpdated(dataUpdated: any) {
    // console.log(JSON.stringify(dataUpdated, null, 2));
    if (dataUpdated.records) {
      const isExistingRecord = this.props.records.find((record) => record.record_id === dataUpdated.records.record_id);
      if (isExistingRecord) {
        // If the record is no longer owned/shared/public for current user, remove it from the view.
        const isOwnedByCurrentUser = dataUpdated.records.saas_tenant_id === this.props.userId;
        const isSharedWithCurrentUser = (dataUpdated.records.sharedSaasTenantIdsAndNames || []).some((shared: any) => shared.id === this.props.userId);
        const isPublicRecord = dataUpdated.records.access_level === RecordAccessLevel.Public;
        if (!isOwnedByCurrentUser && !isPublicRecord && !isSharedWithCurrentUser) {
          this.props.deleteRecordAction(dataUpdated.records.record_id);
          return;
        }
        this.props.setRecordStateAction(dataUpdated.records);
        this.props.setRecordConfirmedByBackendAction(dataUpdated.records.record_id, {
          ai_text: dataUpdated.records.ai_text,
          ...(dataUpdated.records.sharedSaasTenantIdsAndNames ? { sharedSaasTenantIdsAndNames: dataUpdated.records.sharedSaasTenantIdsAndNames } : {}),
          updated_at: dataUpdated.records.updated_at as string,
        });
      } else {
        // Add record to the view if it just became public or newly shared with the current user.
        if (
          dataUpdated.records.access_level === RecordAccessLevel.Public ||
          (dataUpdated.records.sharedSaasTenantIdsAndNames || []).some((shared: { id: string }) => shared.id === this.props.userId)
        ) {
          this.props.addRecordAction(dataUpdated.records);
          this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
        }
      }

      // Latest public records:
      if (dataUpdated.records.access_level === RecordAccessLevel.Public && !dataUpdated.records.is_archived) {
        const latestPublicRecord = {
          updated_at: dataUpdated.records.updated_at || new Date().toISOString(),
          subject: dataUpdated.records.subject || '',
          record_id: dataUpdated.records.record_id,
          isLinkable: dataUpdated.records.saas_tenant_id !== this.props.userId,
          saas_tenant_id: dataUpdated.records.saas_tenant_id || '',
          saas_tenant_name: dataUpdated.records.saas_tenant_name || '',
          summaries: [],
        };
        this.props.addLatestPublicRecordsAction(latestPublicRecord);
      } else {
        // If the record is no longer public, remove it from latest public records
        this.props.removeLatestPublicRecordAction(dataUpdated.records.record_id);
      }

      if (!dataUpdated.records.is_archived) this.props.setCurrentRecordAction(dataUpdated.records.record_id);
    } else if (dataUpdated.saasTenants) {
      this.props.setSaasTenantConfirmedByBackendAction(
        dataUpdated.saasTenants.saas_tenant_id,
        dataUpdated.saasTenants.updated_at as string,
        dataUpdated.saasTenants.name,
      );
      this.props.updateSaasTenantAction(dataUpdated.saasTenants);
      toast.success(this.props.t('saasTenantSettings.messages.updatedSuccessfully'), { autoClose: 2000 });
      // toggle between SaaS Tenant and FFU views
      if (dataUpdated.saasTenants.disabled && this.props.userType === UserType.SaasTenant) {
        this.props.setUserTypeAction(UserType.FFU);
        this.props.setMenuSelectedPageAction(DEFAULT_VIEW);
      } else if (!dataUpdated.saasTenants.disabled && this.props.userType === UserType.FFU) {
        this.props.setUserTypeAction(UserType.SaasTenant);
        this.props.prepareReadRecordsCommandAction();
        this.props.setMenuSelectedPageAction(DEFAULT_VIEW);
      }
    }
  }

  // CRUD: event containing Deleted data
  private handleDataDeleted(dataDeleted: any) {
    // console.log(JSON.stringify(dataDeleted, null, 2));
    if (dataDeleted.records) {
      if (dataDeleted.records.unlinkedBy) {
        this.props.unlinkRecordTenantAction(dataDeleted.records.record_id, dataDeleted.records.unlinkedBy);
      } else {
        this.props.deleteRecordAction(dataDeleted.records.record_id);
      }
      // toast(`${this.props.t('records.fields.record')} ${this.props.t('common.successfullyDeleted')}.`);
    } else if (dataDeleted.subrecords) {
      if (!dataDeleted.subrecords.replaceChatWithConsolidation) {
        const { record_id, subrecord_id } = dataDeleted.subrecords;
        const isExistingSubrecord = !!(this.props.subrecords[record_id] || []).find((subrecord) => subrecord.subrecord_id === subrecord_id);
        if (isExistingSubrecord) this.props.deleteSubrecordAction(record_id, subrecord_id);
      } else {
        // backend\summaries-service\layers\commandsHandlers\commandsHandlers.js.652
        const { record_id, user_text, ai_text, updated_at } = dataDeleted.subrecords.record;
        this.props.setRecordConfirmedByBackendAction(record_id, { user_text, ai_text, updated_at });
      }
    } else if (dataDeleted.saasTenants) {
      // console.log(dataDeleted.saasTenants); //TODO
    }
  }

  // Sets the connections and usernames in the state
  private handleIncomingConnectionsAndUsernames(connections: IConnectionAndUsername[] | null) {
    this.props.setConnectionsAndUsernamesAction(connections);
    // Show the current connections whenever the backend informs this frontend about a change in the connected users:
    if (connections && this.props.userType === UserType.Admin) this.props.toggleConnectionsAction(true);
  }
}

interface IWebSocketProps extends Omit<WithTranslation, 't'> {
  t: (key: string, options?: any) => string;
  JWT: string | null;
  userType: UserType;
  setUserTypeAction: typeof setUserTypeAction;
  userId: string | null;
  userRemainingCredit: number;
  userEmail: string | null;
  userName: string | null;
  isWsConnected: boolean;
  setWSConnectedAction: typeof setWSConnectedAction;
  isAppVisible: boolean;
  setAppVisibleAction: typeof setAppVisibleAction;
  connectionsAndUsernames: IConnectionAndUsername[];
  setConnectionsAndUsernamesAction: typeof setConnectionsAndUsernamesAction;
  showConnections: boolean;
  toggleConnectionsAction: typeof toggleConnectionsAction;
  lastConnectionsTimestamp: string;
  lastConnectionsTimestampISO: string;
  setInitialLoadInProgressAction: typeof setInitialLoadInProgressAction;
  addRecordAction: typeof addRecordAction;
  createCommand: ICreateCommand | null;
  readCommand: IReadCommand | null;
  updateCommand: IUpdateCommand | null;
  deleteCommand: IDeleteCommand | null;
  records: IRecord[];
  showAllLinkable: boolean;
  searchText: string | null;
  latestPublicRecords: ILatestPublicRecord[];
  setLLMProfilesAction: typeof setLLMProfilesAction;
  appendRecordsAction: typeof appendRecordsAction;
  setCurrentRecordAction: typeof setCurrentRecordAction;
  setRecordStateAction: typeof setRecordStateAction;
  deleteRecordAction: typeof deleteRecordAction;
  unlinkRecordTenantAction: typeof unlinkRecordTenantAction;
  setRecordConfirmedByBackendAction: typeof setRecordConfirmedByBackendAction;
  addSubrecordAction: typeof addSubrecordAction;
  deleteSubrecordAction: typeof deleteSubrecordAction;
  setSubrecordAction: typeof setSubrecordAction;
  setSubrecordConfirmedByBackendAction: typeof setSubrecordConfirmedByBackendAction;
  resetRecordsAction: typeof resetRecordsAction;
  prepareReadRecordsCommandAction: typeof prepareReadRecordsCommandAction;
  subrecords: Record<string, ISubrecord[]>;
  menuSelectedPage: string | null;
  setMenuSelectedPageAction: typeof setMenuSelectedPageAction;
  currentRecordId: string | null;
  toggleMenuAction: typeof toggleMenuAction;
  saasTenants: ISaasTenant[];
  prepareReadSaasTenantsCommandAction: typeof prepareReadSaasTenantsCommandAction;
  prepareCreateSaasTenantCommandAction: typeof prepareCreateSaasTenantCommandAction;
  setSaasTenantsAction: typeof setSaasTenantsAction;
  addSaasTenantAction: typeof addSaasTenantAction;
  updateSaasTenantAction: typeof updateSaasTenantAction;
  setSaasTenantConfirmedByBackendAction: typeof setSaasTenantConfirmedByBackendAction;
  setSaasTenantSearchResultsAction: typeof setSaasTenantSearchResultsAction;
  setSaasTenantSearchTooManyResultsAction: typeof setSaasTenantSearchTooManyResultsAction;
  setViewModeAction: typeof setViewModeAction;
  updateSaasTenantUsageAction: typeof updateSaasTenantUsageAction;
  setLatestPublicRecordsAction: typeof setLatestPublicRecordsAction;
  updateLatestPublicRecordAction: typeof updateLatestPublicRecordAction;
  addLatestPublicRecordsAction: typeof addLatestPublicRecordsAction;
  removeLatestPublicRecordAction: typeof removeLatestPublicRecordAction;
  setUsageMetricsDataAction: typeof setUsageMetricsDataAction;
}

interface WebSocketState {
  previousMenuSelectedPage: string | null;
}

// Maps required state from Redux store to component props
const mapStateToProps = (state: IAppState) => {
  const currentSaasTenant = state.saasTenants.saasTenants.find((tenant) => tenant.saas_tenant_id === state.auth.userId) as ISaasTenant;
  return {
    JWT: state.auth.JWT,
    userType: state.auth.userType,
    userId: state.auth.userId,
    userRemainingCredit: (currentSaasTenant && (currentSaasTenant.credit as number) - ((currentSaasTenant.usage?.total_cost as number) || 0)) || 0,
    userEmail: state.auth.email,
    userName: state.auth.userName,
    isWsConnected: state.websockets.isConnected,
    isAppVisible: state.websockets.isAppVisible,
    connectionsAndUsernames: state.websockets.connectionsAndUsernames,
    showConnections: state.websockets.showConnections,
    lastConnectionsTimestamp: state.websockets.lastConnectionsTimestamp,
    lastConnectionsTimestampISO: state.websockets.lastConnectionsTimestampISO,
    createCommand: state.crud.createCommand,
    readCommand: state.crud.readCommand,
    updateCommand: state.crud.updateCommand,
    deleteCommand: state.crud.deleteCommand,
    records: state.records.records,
    showAllLinkable: state.records.showAllLinkable,
    searchText: state.records.searchText,
    latestPublicRecords: state.records.latestPublicRecords,
    currentRecordId: state.records.currentRecordId,
    subrecords: state.subrecords.subrecords,
    menuSelectedPage: state.menu.menuSelectedPage, // TODO
    saasTenants: state.saasTenants.saasTenants,
  };
};

// Map Redux actions to component props
const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setWSConnectedAction,
      setAppVisibleAction,
      setConnectionsAndUsernamesAction,
      toggleConnectionsAction,
      setLLMProfilesAction,
      setInitialLoadInProgressAction,
      appendRecordsAction,
      setCurrentRecordAction,
      addRecordAction,
      setRecordStateAction,
      deleteRecordAction,
      unlinkRecordTenantAction,
      setRecordConfirmedByBackendAction,
      resetRecordsAction,
      prepareReadRecordsCommandAction,
      addSubrecordAction,
      deleteSubrecordAction,
      setSubrecordAction,
      setSubrecordConfirmedByBackendAction,
      setUserTypeAction,
      setMenuSelectedPageAction,
      toggleMenuAction,
      prepareCreateSaasTenantCommandAction,
      prepareReadSaasTenantsCommandAction,
      setSaasTenantsAction,
      addSaasTenantAction,
      updateSaasTenantAction,
      setSaasTenantConfirmedByBackendAction,
      setSaasTenantSearchResultsAction,
      setSaasTenantSearchTooManyResultsAction,
      setViewModeAction,
      updateSaasTenantUsageAction,
      setLatestPublicRecordsAction,
      updateLatestPublicRecordAction,
      addLatestPublicRecordsAction,
      removeLatestPublicRecordAction,
      setUsageMetricsDataAction,
    },
    dispatch,
  );

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(WebSocketService));
